"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useRef, useEffect } from "react";
import mapboxgl, { LngLat, MapOptions } from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { FeatureCollection, Geometry } from "geojson";
import { toaster } from "@/components/ui/toaster";
import { LayerToggleObjProps } from "./address-mapper";
import { Box } from "@chakra-ui/react";
import system from "../../styles/theme";
import { resolveColorToken } from "../../styles/resolve-color-token";
const mapOptions: Omit<MapOptions, "container"> = {
  style: "mapbox://styles/mapbox/standard",
  zoom: 12.1,
  // Start with more zoomed-out view but not too far
  minZoom: 11,
  // Allow users to zoom out more
  maxZoom: 15,
  // Increase max zoom to allow closer inspection
  maxBounds: [[-122.6, 37.65],
  // Southwest coordinates
  [-122.25, 37.85] // Northeast coordinates
  ],
  dragRotate: false,
  // turn off rotation on drag
  touchPitch: false,
  // turn off pitch change w/touch
  touchZoomRotate: true,
  // turn on zoom/rotate w/touch
  config: {
    // Initial configuration for the Mapbox Standard style set above. By default, its ID is `basemap`.
    basemap: {
      // 'default', 'faded', or 'monochrome'
      theme: "monochrome",
      lightPreset: "day",
      colorRoads: "#fefefe" // matches the lightPreset "dawn" basemap so roads appear invisible with theme "monochrome"
    }
  }
};
interface MapProps {
  lon: number;
  lat: number;
  address: string | null;
  softStoryData: FeatureCollection<Geometry>;
  tsunamiData: FeatureCollection<Geometry>;
  liquefactionData: FeatureCollection<Geometry>;
  femaRiskData: FeatureCollection<Geometry>;
  layerToggleObj: LayerToggleObjProps;
  /** Fraction of the container height covered at the bottom. */
  bottomPaddingRatio?: number;
}
const addMarker = (center: LngLat, map: mapboxgl.Map) => {
  const el = document.createElement("div");
  return new mapboxgl.Marker({
    anchor: "bottom",
    element: el,
    className: "marker"
  }).setLngLat(center).addTo(map);
};
const Map: React.FC<MapProps> = t0 => {
  const $ = _c(27);
  const {
    lon,
    lat,
    address,
    softStoryData,
    tsunamiData,
    liquefactionData,
    femaRiskData,
    layerToggleObj,
    bottomPaddingRatio: t1
  } = t0;
  const bottomPaddingRatio = t1 === undefined ? 0 : t1;
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const lastLon = useRef(lon);
  const lastLat = useRef(lat);
  const ratioRef = useRef(0);
  const tsunamiVisibilityRef = useRef("visible");
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ratio => Math.round((mapContainerRef.current?.clientHeight ?? 0) * ratio);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const bottomPaddingPx = t2;
  let t3;
  let t4;
  if ($[1] !== bottomPaddingRatio) {
    t3 = () => {
      ratioRef.current = bottomPaddingRatio;
      const map = mapRef.current;
      if (!map) {
        return;
      }
      const bottom = bottomPaddingPx(bottomPaddingRatio);
      if (map.getPadding().bottom !== bottom) {
        map.easeTo({
          padding: {
            bottom
          },
          duration: map.loaded() ? 750 : 0
        });
      }
    };
    t4 = [bottomPaddingRatio];
    $[1] = bottomPaddingRatio;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== address || $[5] !== femaRiskData || $[6] !== lat || $[7] !== liquefactionData || $[8] !== lon || $[9] !== tsunamiData) {
    t5 = () => {
      const mapboxToken = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
      if (!mapContainerRef.current || !mapboxToken) {
        if (!toaster.isVisible("no-token")) {
          toaster.create({
            id: "no-token",
            description: "Mapbox access token or container is not set!",
            type: "error",
            duration: 5000,
            closable: true
          });
        }
        console.error("Mapbox access token or container is not set!");
        return;
      }
      mapboxgl.accessToken = mapboxToken;
      const center = lon && lat ? new LngLat(lon, lat) : null;
      if (!mapRef.current) {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          ...(center ? {
            center
          } : {}),
          ...mapOptions
        });
        const map_0 = mapRef.current;
        map_0.touchZoomRotate.disableRotation();
        const nav = new mapboxgl.NavigationControl({
          showCompass: false
        });
        map_0.addControl(nav, "bottom-right");
        map_0.setPadding({
          bottom: bottomPaddingPx(ratioRef.current)
        });
        if (center && address) {
          markerRef.current = addMarker(center, map_0);
        }
        map_0.on("load", () => {
          const tsunamiColor = resolveColorToken("colors.tsunami");
          map_0.addSource("seismic", {
            type: "geojson",
            data: liquefactionData
          });
          map_0.addSource("tsunami", {
            type: "geojson",
            data: tsunamiData
          });
          map_0.addSource("fema-risk", {
            type: "geojson",
            data: femaRiskData
          });
          map_0.addLayer({
            id: "femaRiskLayer",
            source: "fema-risk",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": resolveColorToken("colors.femaRisk"),
              "fill-opacity": ["match", ["get", "fema_risk_rating"], "Relatively Low", 0.015, "Relatively Moderate", 0.035, "Relatively High", 0.08, "Very High", 0.18, 0]
            }
          });
          map_0.addLayer({
            id: "seismicBackgroundLayer",
            source: "seismic",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": system.token("colors.orange.300"),
              "fill-opacity": 0.04
            }
          });
          map_0.addLayer({
            id: "seismicBorderOuterLayer",
            source: "seismic",
            type: "line",
            slot: "middle",
            paint: {
              "line-color": system.token("colors.orange.600"),
              "line-width": 2,
              "line-offset": -1,
              "line-opacity": 0.9
            }
          });
          map_0.addLayer({
            id: "seismicBorderInnerLayer",
            source: "seismic",
            type: "line",
            slot: "middle",
            paint: {
              "line-color": system.token("colors.orange.300"),
              "line-width": 6,
              "line-offset": 3,
              "line-opacity": 0.35,
              "line-blur": 0.75
            }
          });
          map_0.addLayer({
            id: "tsunamiInnerLayer",
            source: "tsunami",
            type: "fill",
            slot: "middle",
            paint: {
              "fill-color": tsunamiColor,
              "fill-opacity": 0.25
            }
          });
          map_0.loadImage("/images/tsunami-hatch-fine-16.png", (error, image) => {
            if (error) {
              console.error("Failed to load tsunami hatch:", error);
              return;
            }
            if (!image) {
              return;
            }
            if (!map_0.hasImage("tsunami-hatch")) {
              map_0.addImage("tsunami-hatch", image);
              map_0.addLayer({
                id: "tsunamiLayer",
                source: "tsunami",
                type: "fill",
                slot: "middle",
                paint: {
                  "fill-pattern": "tsunami-hatch"
                }
              });
              map_0.setLayoutProperty("tsunamiLayer", "visibility", tsunamiVisibilityRef.current);
            }
          });
          map_0.on("error", _temp);
        });
        const updateBasemapDetail = () => {
          const detailed = map_0.getZoom() >= 13;
          map_0.setConfigProperty("basemap", "showRoadLabels", detailed);
          map_0.setConfigProperty("basemap", "showPedestrianRoads", detailed);
          map_0.setConfigProperty("basemap", "showPointOfInterestLabels", detailed);
          map_0.setConfigProperty("basemap", "colorRoads", detailed ? "#cccccc" : "#fefefe");
        };
        map_0.on("zoomend", updateBasemapDetail);
        updateBasemapDetail();
      } else {
        const map_1 = mapRef.current;
        if (center) {
          if (address) {
            if (!markerRef.current) {
              markerRef.current = addMarker(center, map_1);
            }
            markerRef.current.setLngLat(center);
          } else {
            if (markerRef.current) {
              markerRef.current?.remove();
              markerRef.current = null;
            }
          }
          if (map_1.getCenter().lng !== center.lng || map_1.getCenter().lat !== center.lat) {
            map_1.easeTo({
              center,
              padding: {
                bottom: bottomPaddingPx(ratioRef.current)
              },
              duration: 750
            });
            lastLon.current = lon;
            lastLat.current = lat;
          }
        }
        return;
      }
    };
    $[4] = address;
    $[5] = femaRiskData;
    $[6] = lat;
    $[7] = liquefactionData;
    $[8] = lon;
    $[9] = tsunamiData;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== address || $[12] !== femaRiskData || $[13] !== lat || $[14] !== liquefactionData || $[15] !== lon || $[16] !== softStoryData || $[17] !== tsunamiData) {
    t6 = [lon, lat, address, liquefactionData, softStoryData, tsunamiData, femaRiskData];
    $[11] = address;
    $[12] = femaRiskData;
    $[13] = lat;
    $[14] = liquefactionData;
    $[15] = lon;
    $[16] = softStoryData;
    $[17] = tsunamiData;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  useEffect(t5, t6);
  let t7;
  if ($[19] !== layerToggleObj.layerIds || $[20] !== layerToggleObj.toggleState) {
    t7 = () => {
      if (layerToggleObj.layerIds.includes("tsunamiLayer")) {
        tsunamiVisibilityRef.current = layerToggleObj.toggleState ? "visible" : "none";
      }
      const handleToggleLayers = () => {
        if (!mapRef.current) {
          return;
        }
        const map_2 = mapRef.current;
        const newVisibility = layerToggleObj.toggleState ? "visible" : "none";
        layerToggleObj.layerIds.forEach(layerId => {
          if (map_2.getLayer(layerId)) {
            map_2.setLayoutProperty(layerId, "visibility", newVisibility);
          }
        });
      };
      if (layerToggleObj.layerIds.length > 0) {
        handleToggleLayers();
      }
    };
    $[19] = layerToggleObj.layerIds;
    $[20] = layerToggleObj.toggleState;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== layerToggleObj) {
    t8 = [layerToggleObj];
    $[22] = layerToggleObj;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const container = mapContainerRef.current;
      if (!container || typeof ResizeObserver === "undefined") {
        return;
      }
      const observer = new ResizeObserver(() => {
        const map_3 = mapRef.current;
        if (!map_3) {
          return;
        }
        map_3.resize();
        const bottom_0 = bottomPaddingPx(ratioRef.current);
        if (map_3.getPadding().bottom !== bottom_0) {
          map_3.setPadding({
            bottom: bottom_0
          });
        }
      });
      observer.observe(container);
      return () => observer.disconnect();
    };
    t10 = [];
    $[24] = t10;
    $[25] = t9;
  } else {
    t10 = $[24];
    t9 = $[25];
  }
  useEffect(t9, t10);
  let t11;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Box ref={mapContainerRef} w="full" h="full" />;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  return t11;
};
export default Map;
function _temp(e) {
  if (e.error && e.error.message.includes("access token")) {
    if (!toaster.isVisible("invalid-token")) {
      toaster.create({
        id: "invalid-token",
        description: "Invalid Mapbox access token!",
        type: "error",
        duration: 5000,
        closable: true
      });
    }
    console.error("Invalid Mapbox token:", e.error);
  }
}